import { useState } from 'react'; 
import { useNavigate } from 'react-router-dom';
import { 
  User, ClipboardList,  Pill,MessageSquare, Headset, 
  Users, HelpCircle, Info, FileText, Shield, PhoneCall, 
  LogOut, ChevronRight, Loader2
} from 'lucide-react';
import type { UserProfile } from './UserNavbar';
import { SupportContactModal } from '../../dashboards/SupportContactModal'; // Make sure path is correct!

interface UserDropdownProps {
  profile?: UserProfile | null;
  logout: () => void;
  isLoggingOut: boolean;
  closeDropdown: () => void;
}

export default function UserDropdown({ profile, logout, isLoggingOut, closeDropdown }: UserDropdownProps) {
  const navigate = useNavigate();
  const [isSupportModalOpen, setIsSupportModalOpen] = useState(false); 

 const menuItems = [
    { label: 'My Profile', icon: User, path: '/user/profile' },
    { label: 'Orders', icon: ClipboardList, path: '/user/orders' },
    // { label: 'My Queue List', icon: ListTodo, path: '/user/queue' },
    { label: 'Chat History', icon: MessageSquare, path: '/user/messages' },
    { label: 'My Records', icon: Pill, path: '/user/prescriptions' }, // 🟢 Added Digital Prescription
    { label: 'Customer Support Chat', icon: Headset, path: '#support' }, 
    { label: 'Followings', icon: Users, path: '/user/followings' },
    { label: 'FAQ', icon: HelpCircle, path: '/faq' },
    { label: 'About trueAdviso', icon: Info, path: '/about' },
    { label: 'Term And Condition', icon: FileText, path: '/terms' },
    { label: 'Privacy Policy', icon: Shield, path: '/privacy-policy' },
    { label: 'Contact Us', icon: PhoneCall, path: '/contact' },
  ];

  // 🟢 Pass the event (e) here so we can stop it from bubbling
  const handleNavigation = (e: React.MouseEvent, path: string) => {
    e.stopPropagation();
    
    if (path === '#support') {
      setIsSupportModalOpen(true);
      return; 
    }

    if (path.startsWith('http')) {
      window.open(path, '_blank', 'noopener,noreferrer');
    } else {
      navigate(path);
    }
    closeDropdown();
  };

  const handleModalClose = () => {
    setIsSupportModalOpen(false);
    closeDropdown(); 
  };

  return (
    <>
      <div 
        className="absolute right-0 mt-2 w-72 bg-white border border-gray-100 rounded-2xl shadow-xl z-50 flex flex-col overflow-hidden"
        style={{ maxHeight: '70vh' }} 
        // 🟢 Stop clicks inside the dropdown from closing it accidentally
        onMouseDown={(e) => e.stopPropagation()} 
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b border-gray-100 bg-gray-50/50 shrink-0">
          <p className="text-sm font-bold text-gray-800 truncate">{profile?.name || 'User'}</p>
          <p className="text-xs text-gray-500 truncate">{profile?.userCode || 'Member'}</p>
        </div>
        
        <div className="flex-1 overflow-y-auto overscroll-contain py-2 custom-scrollbar">
          {menuItems.map((item, index) => (
            <button 
              key={index}
              onClick={(e) => handleNavigation(e, item.path)} // 🟢 Attach event here
              className="w-full flex items-center justify-between px-5 py-3 border-b border-gray-50/50 border-l-2 border-l-transparent hover:bg-gray-50 hover:border-l-purple-400 transition-all duration-200 group"
            >
              <div className="flex items-center gap-4">
                <div className="w-9 h-9 rounded-xl bg-purple-50 flex items-center justify-center text-purple-500 group-hover:scale-110 transition-transform duration-200">
                  <item.icon className="w-4 h-4" />
                </div>
                <span className="text-sm font-medium text-gray-700">{item.label}</span>
              </div>
              <ChevronRight className="w-4 h-4 text-purple-500 transition-transform duration-200 group-hover:translate-x-0.5" />
            </button>
          ))}

          <button 
            onClick={(e) => {
              e.stopPropagation();
              logout();
            }}
            disabled={isLoggingOut}
            className="w-full hidden sm:flex items-center justify-between px-5 py-3 border-l-2 border-l-transparent hover:bg-red-50/50 hover:border-l-red-400 transition-all duration-200 group disabled:opacity-50 mt-1 mb-2"
          >
            <div className="flex items-center gap-4">
              <div className="w-9 h-9 rounded-xl bg-red-50 flex items-center justify-center text-red-500 group-hover:scale-110 transition-transform duration-200">
                {isLoggingOut ? <Loader2 className="w-4 h-4 animate-spin text-red-500" /> : <LogOut className="w-4 h-4 text-red-500" />}
              </div>
              <span className="text-sm font-medium text-red-500">
                {isLoggingOut ? 'Logging out...' : 'Logout'}
              </span>
            </div>
            {!isLoggingOut && <ChevronRight className="w-4 h-4 text-red-500 transition-transform duration-200 group-hover:translate-x-0.5" />}
          </button>
        </div>
      </div>

      <SupportContactModal 
        isOpen={isSupportModalOpen}
        onClose={handleModalClose}
        role="user"
        name={profile?.name || 'User'}
        code={profile?.userCode || 'N/A'}
      />
    </>
  );
}